import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import type { Analysis } from '@fishymeter/types';
import { api, ApiError } from '@/lib/api';
import { AnalysisResult } from '@/components/analysis-result';
import { ShareButton } from '@/components/share-button';

export function CatchPage() {
  const { id } = useParams<{ id: string }>();
  const auth = useAuth();
  // Wait for the OIDC client to finish hydrating its stored token before
  // firing the status query. Otherwise on a hard refresh of `/c/{id}` the
  // query fires before the JWT is available, the request goes out with no
  // Authorization header, and the privacy-gated API returns 404.
  const authReady = !auth.isLoading;
  const status = useQuery({
    queryKey: ['catch', id, auth.isAuthenticated],
    queryFn: () => api.getStatus(id!),
    enabled: !!id && authReady,
    retry: (count, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return count < 1;
    },
  });

  if (!id) return <NotAvailable reason="missing-id" />;
  if (!authReady || status.isLoading) {
    return <div className="p-12 text-center text-sm text-muted-foreground">Loading…</div>;
  }
  if (status.error) {
    if (status.error instanceof ApiError && status.error.status === 404) {
      return <NotAvailable reason="not-found" />;
    }
    return <NotAvailable reason="error" message={(status.error as Error).message} />;
  }
  if (!status.data) return null;

  if (status.data.status === 'pending' || status.data.status === 'processing') {
    return (
      <div className="mx-auto max-w-md px-4 py-16 text-center">
        <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-muted border-t-primary" />
        <p className="mt-4 text-sm text-muted-foreground">
          This catch is still scoring. Refresh in a moment.
        </p>
      </div>
    );
  }
  if (status.data.status === 'failed') {
    return (
      <NotAvailable
        reason="failed"
        message={'error_message' in status.data ? status.data.error_message : undefined}
      />
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
      <AnalysisResult
        analysis={status.data as unknown as Analysis}
        actions={
          <ShareButton
            analysisId={id}
            initialIsPublic={(status.data as unknown as Analysis).is_public ?? false}
          />
        }
      />

      <div className="mt-10 text-center">
        <Link to="/" className="text-sm text-primary underline">
          Score something else →
        </Link>
      </div>
    </div>
  );
}

function NotAvailable({
  reason,
  message,
}: {
  reason: 'not-found' | 'failed' | 'error' | 'missing-id';
  message?: string;
}) {
  const titles: Record<typeof reason, string> = {
    'not-found': 'Catch not found',
    failed: 'Analysis failed',
    error: "Couldn't load this catch",
    'missing-id': 'Catch not specified',
  };
  const bodies: Record<typeof reason, string> = {
    'not-found':
      'Either this catch was made private by its author, or the link is wrong.',
    failed:
      message ?? "Something went wrong while scoring this content.",
    error:
      message ?? 'Try again in a moment.',
    'missing-id': 'No catch ID in the URL.',
  };
  return (
    <div className="mx-auto max-w-md px-4 py-16 text-center">
      <h1 className="text-2xl font-bold">{titles[reason]}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{bodies[reason]}</p>
      <Link to="/" className="mt-6 inline-block text-sm text-primary underline">
        Back to homepage
      </Link>
    </div>
  );
}
